import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import {
  clearPasskeyOptIn,
  completePasskeyRegistration,
  deletePasskey,
  getPasskeyOptIn,
  isPasskeyAlreadyRegisteredError,
  isPasskeyCancelledError,
  isSameCredential,
  isPasskeySupported,
  listPasskeys,
  savePasskeyOptIn,
  startPasskeyRegistration,
} from "../helpers/passkeys";

const formatCreatedAt = (value, language) => {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(language === "en" ? "en-GB" : "pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Opt-in for passkey sign-in on this device. Off keeps the email + password + code login.
// `embedded` renders it as a sub-block of the Profile "Forma de entrar" card. There, Face ID
// accounts have no password: `requiresCodeOnlySwitch` asks for confirmation first and
// `onSwitchToCodeOnly` (resolves true when done) moves the account to CODE_ONLY before
// registering. `onPasskeysChange` receives the account's passkeys after every load;
// `onPasskeysMutated` runs after a passkey is registered or deleted.
const PasskeySettings = ({
  embedded = false,
  requiresCodeOnlySwitch = false,
  onSwitchToCodeOnly,
  onPasskeysChange,
  onPasskeysMutated,
}) => {
  const { user, tokens } = useAuth();
  const { t, language } = useTranslation();
  const accessToken = tokens?.accessToken;
  const email = user?.email || user?.username || "";

  const [passkeys, setPasskeys] = useState([]);
  const [optIn, setOptIn] = useState(() => getPasskeyOptIn(email));
  const [creationOptions, setCreationOptions] = useState(null);
  // "loading" | "adding" | "removing" | null
  const [busy, setBusy] = useState("loading");
  const busyRef = useRef(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const onPasskeysChangeRef = useRef(onPasskeysChange);
  onPasskeysChangeRef.current = onPasskeysChange;
  const onPasskeysMutatedRef = useRef(onPasskeysMutated);
  onPasskeysMutatedRef.current = onPasskeysMutated;

  const thisDeviceId = optIn?.credentialId || null;
  const enabled = Boolean(optIn);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    try {
      const list = await listPasskeys(accessToken);
      setPasskeys(list);
      onPasskeysChangeRef.current?.(list);
      const stored = getPasskeyOptIn(email);
      // The passkey was removed elsewhere (another device or support): this device is opted out.
      if (stored?.credentialId && !list.some((p) => isSameCredential(p.CredentialId, stored.credentialId))) {
        clearPasskeyOptIn(email);
        setOptIn(null);
      } else {
        setOptIn(stored);
      }
      // Fetch options ahead of time so the toggle tap can call WebAuthn create() directly.
      setCreationOptions(stored ? null : await startPasskeyRegistration(accessToken));
    } catch (e) {
      console.error("Passkey list/options failed", e);
      setError("passkey.loadFailed");
    }
  }, [accessToken, email]);

  useEffect(() => {
    let active = true;
    refresh().finally(() => {
      if (active) setBusy(null);
    });
    return () => {
      active = false;
    };
  }, [refresh]);

  const runExclusive = async (kind, action) => {
    // A ref, not state, so rapid repeated taps can't start a second WebAuthn prompt.
    if (busyRef.current || !accessToken) return false;
    busyRef.current = true;
    setBusy(kind);
    setError("");
    setInfo([]);
    try {
      await action();
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
    return true;
  };

  const afterChange = async (mutated) => {
    await refresh();
    if (mutated) await onPasskeysMutatedRef.current?.();
  };

  const enable = async ({ switchToCodeOnly = false } = {}) => {
    let mutated = false;
    const ran = await runExclusive("adding", async () => {
      if (switchToCodeOnly && onSwitchToCodeOnly && !(await onSwitchToCodeOnly())) return;
      try {
        const options = creationOptions || (await startPasskeyRegistration(accessToken));
        setCreationOptions(null);
        const credentialId = await completePasskeyRegistration(accessToken, options);
        mutated = true;
        savePasskeyOptIn(email, credentialId);
        setOptIn(getPasskeyOptIn(email));
        setInfo(["passkey.enabled"]);
      } catch (e) {
        console.error("Passkey registration failed", e);
        setError(
          isPasskeyCancelledError(e)
            ? "passkey.cancelled"
            : isPasskeyAlreadyRegisteredError(e)
              ? "passkey.alreadyRegistered"
              : "passkey.enableFailed",
        );
      }
    });
    if (ran) await afterChange(mutated);
  };

  const disable = async () => {
    let mutated = false;
    const ran = await runExclusive("removing", async () => {
      try {
        const thisDevice = passkeys.find((p) => isSameCredential(p.CredentialId, thisDeviceId));
        if (thisDevice) {
          await deletePasskey(accessToken, thisDevice.CredentialId);
          mutated = true;
        }
        clearPasskeyOptIn(email);
        setOptIn(null);
        const others = passkeys.filter((p) => !isSameCredential(p.CredentialId, thisDeviceId));
        setInfo(others.length ? ["passkey.disabled", "passkey.otherDevicesHint"] : ["passkey.disabled"]);
      } catch (e) {
        console.error("Passkey removal failed", e);
        setError("passkey.disableFailed");
      }
    });
    if (ran) await afterChange(mutated);
  };

  const removeDevice = async (credentialId) => {
    let mutated = false;
    const ran = await runExclusive("removing", async () => {
      try {
        await deletePasskey(accessToken, credentialId);
        mutated = true;
        if (isSameCredential(credentialId, thisDeviceId)) {
          clearPasskeyOptIn(email);
          setOptIn(null);
        }
        setInfo(["passkey.removed"]);
      } catch (e) {
        console.error("Passkey removal failed", e);
        setError("passkey.disableFailed");
      }
    });
    if (ran) await afterChange(mutated);
  };

  const handleToggle = (event) => {
    if (!event.target.checked) {
      disable();
    } else if (embedded && requiresCodeOnlySwitch) {
      setError("");
      setInfo([]);
      setConfirming(true);
    } else {
      enable();
    }
  };

  const confirmEnable = () => {
    setConfirming(false);
    enable({ switchToCodeOnly: true });
  };

  const supported = isPasskeySupported();

  const Container = embedded ? "div" : "section";
  const containerClass = embedded
    ? "passkey-settings passkey-settings--embedded"
    : "portal-page-panel passkey-settings";
  const otherDevicesOnly = embedded && !enabled && busy !== "loading" && passkeys.length > 0;

  return (
    <Container className={containerClass} aria-busy={Boolean(busy)}>
      {embedded ? (
        <h4 className="portal-label passkey-subtitle">{t("passkey.sectionTitle")}</h4>
      ) : (
        <h3 className="portal-label">{t("passkey.title")}</h3>
      )}

      {!supported ? (
        <p className="passkey-unsupported">{t("passkey.unsupported")}</p>
      ) : (
        <>
          <p>{t(embedded ? "passkey.shortDescription" : "passkey.description")}</p>

          <label className="passkey-toggle">
            <input
              type="checkbox"
              checked={enabled}
              onChange={handleToggle}
              disabled={Boolean(busy) || confirming || !accessToken}
            />
            <span>
              {!embedded
                ? t("passkey.toggleLabel")
                : t(enabled ? "passkey.activeToggleLabel" : "passkey.addToggleLabel")}
            </span>
          </label>

          {confirming && (
            <div className="passkey-confirm" role="alertdialog" aria-labelledby="passkey-confirm-text">
              <p id="passkey-confirm-text">{t("passkey.codeOnlyConfirm")}</p>
              <div className="portal-inline-form-controls">
                <button type="button" className="portal-button" onClick={confirmEnable}>
                  {t("passkey.confirmEnable")}
                </button>
                <button type="button" className="portal-button" onClick={() => setConfirming(false)}>
                  {t("passkey.cancelEnable")}
                </button>
              </div>
            </div>
          )}

          {busy === "adding" && (
            <p className="passkey-status" role="status">{t("passkey.waiting")}</p>
          )}
          {busy === "loading" && (
            <p className="passkey-status" role="status">{t("passkey.loading")}</p>
          )}

          {error && <p className="error" role="alert">{t(error)}</p>}
          {info.map((key) => (
            <p key={key} className="passkey-status success" role="status">{t(key)}</p>
          ))}

          {passkeys.length > 0 && (
            <div className="passkey-devices">
              <p className="portal-label">
                {t(embedded ? "passkey.registeredTitle" : "passkey.devicesTitle")}
              </p>
              {otherDevicesOnly && (
                <p className="passkey-status passkey-hint">{t("passkey.otherDevicesOnly")}</p>
              )}
              <ul>
                {passkeys.map((passkey) => {
                  const created = formatCreatedAt(passkey.CreatedAt, language);
                  const isThisDevice = isSameCredential(passkey.CredentialId, thisDeviceId);
                  return (
                    <li key={passkey.CredentialId} className="passkey-device">
                      <span>
                        {passkey.FriendlyCredentialName || "Passkey"}
                        {isThisDevice && <strong> ({t("passkey.thisDevice")})</strong>}
                        {created && ` · ${t("passkey.createdOn")} ${created}`}
                      </span>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => removeDevice(passkey.CredentialId)}
                        disabled={Boolean(busy)}
                      >
                        {t("passkey.remove")}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default PasskeySettings;
